/**
 * Upcoming Tasks Component
 *
 * Consolidated action items card showing everything that needs attention:
 * - Summary counters (urgent items, drafts, ready to bill, overdue)
 * - Detailed ball-in-court items list
 *
 * Replaces separate UrgentActionsCard + BallInCourtMini components
 */
import {
  useDashboardStats,
  useCashFlowForecast,
  useOutstandingInvoices,
  useBallInCourt,
  type BallInCourtItem,
} from '@/hooks/useDashboardReports';
import {
  AlertCircle,
  FileText,
  DollarSign,
  Clock,
  AlertTriangle,
  ArrowRight,
  ListTodo,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface UpcomingTasksProps {
  maxBallInCourtItems?: number;
}

export function UpcomingTasks({ maxBallInCourtItems = 5 }: UpcomingTasksProps) {
  const navigate = useNavigate();
  const { data: statsData, isLoading: statsLoading } = useDashboardStats();
  const { data: cashFlowData, isLoading: cashLoading } = useCashFlowForecast(90);
  const { data: invoicesData, isLoading: invoicesLoading } = useOutstandingInvoices();
  const { data: ballData, isLoading: ballLoading } = useBallInCourt();

  const isLoading = statsLoading || cashLoading || invoicesLoading || ballLoading;

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-5">
        <div className="space-y-4 animate-pulse">
          {/* Summary skeleton */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3">
                <div className="h-3 bg-gray-200 rounded w-16 mb-2"></div>
                <div className="h-6 bg-gray-100 rounded w-10"></div>
              </div>
            ))}
          </div>
          {/* Items skeleton */}
          <div className="space-y-2 pt-3 border-t">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="h-4 w-4 bg-gray-200 rounded mt-1"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const stats = statsData as any;
  const cashFlow = cashFlowData as any;
  const invoices = invoicesData as any;
  const ball = ballData as any;

  // Calculate action counts
  const urgentBallInCourt =
    (ball?.summary?.waiting_on_us_urgent || 0) +
    (ball?.summary?.waiting_on_them_urgent || 0);
  const draftInvoices = stats?.stats?.draft_invoices || 0;
  const availableToBill = cashFlow?.available_to_bill?.total_amount || 0;
  const overdueInvoices = invoices?.summary?.overdue_count || 0;

  // Get ball-in-court items
  const waitingOnUs = ball?.waiting_on_us || [];
  const waitingOnThem = ball?.waiting_on_them || [];
  const allItems: BallInCourtItem[] = [...waitingOnUs, ...waitingOnThem];
  const sortedItems = allItems
    .sort((a, b) => {
      // Urgent items first
      if (a.urgency === 'high' && b.urgency !== 'high') return -1;
      if (b.urgency === 'high' && a.urgency !== 'high') return 1;
      // Then by days waiting (longest first)
      return b.days_waiting - a.days_waiting;
    })
    .slice(0, maxBallInCourtItems);

  const summaryActions = [
    {
      icon: AlertCircle,
      label: 'Urgent',
      value: urgentBallInCourt,
      color: urgentBallInCourt > 0 ? 'text-rose-700 bg-rose-50' : 'text-slate-400 bg-slate-50',
      link: '/reports?tab=ball-in-court',
    },
    {
      icon: FileText,
      label: 'Drafts',
      value: draftInvoices,
      color: draftInvoices > 0 ? 'text-sky-700 bg-sky-50' : 'text-slate-400 bg-slate-50',
      link: '/invoices?status=draft',
    },
    {
      icon: DollarSign,
      label: 'To Bill',
      value: formatCurrency(availableToBill),
      color: availableToBill > 0 ? 'text-emerald-700 bg-emerald-50' : 'text-slate-400 bg-slate-50',
      link: '/reports?tab=drilldowns&view=available-to-invoice-by-project',
    },
    {
      icon: Clock,
      label: 'Overdue',
      value: overdueInvoices,
      color: overdueInvoices > 0 ? 'text-amber-700 bg-amber-50' : 'text-slate-400 bg-slate-50',
      link: '/reports?tab=outstanding-invoices',
    },
  ];

  const hasAnyActions =
    urgentBallInCourt > 0 || draftInvoices > 0 || availableToBill > 0 || overdueInvoices > 0;

  return (
    <div className="bg-white rounded-lg shadow p-5" data-testid="upcoming-tasks">
      <div className="flex items-center justify-end mb-4">
        {hasAnyActions && (
          <Link
            to="/reports?tab=ball-in-court"
            className="text-xs text-slate-600 hover:underline flex items-center gap-1"
          >
            View all
            <ArrowRight size={12} />
          </Link>
        )}
      </div>

      {/* Summary Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {summaryActions.map((action) => {
          const Icon = action.icon;
          const [textColor, bgColor] = action.color.split(' ');
          const hasValue =
            typeof action.value === 'number' ? action.value > 0 : action.value !== '$0';

          return (
            <Link
              key={action.label}
              to={action.link}
              className={`${bgColor} rounded-lg p-3 transition-all ${
                hasValue ? 'hover:shadow-md cursor-pointer' : 'opacity-60'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon size={14} className={textColor} />
                <span className="text-xs font-medium text-gray-600">{action.label}</span>
              </div>
              <div className={`text-xl font-bold ${textColor}`}>{action.value}</div>
            </Link>
          );
        })}
      </div>

      {/* Ball in Court Items */}
      {sortedItems.length > 0 ? (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">Ball in Court</h3>
            <div className="text-xs text-gray-500">
              <span className="inline-block w-2 h-2 rounded-full bg-sky-500 mr-1"></span>
              Us
              <span className="inline-block w-2 h-2 rounded-full bg-violet-500 ml-2 mr-1"></span>
              Them
            </div>
          </div>
          <div className="space-y-2">
            {sortedItems.map((item) => {
              const isUrgent = item.urgency === 'high';
              const isWaitingOnUs = item.status === 'waiting_on_us';

              return (
                <div
                  key={item.scope_id}
                  data-testid={`ball-in-court-item-${item.scope_id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/projects/${item.project_id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate(`/projects/${item.project_id}`);
                    }
                  }}
                  className={`flex items-start gap-2 p-2.5 rounded-lg text-sm cursor-pointer transition-colors ${
                    isUrgent
                      ? 'bg-rose-50 border border-rose-200 hover:bg-rose-100'
                      : 'bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  {isUrgent && (
                    <AlertTriangle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
                  )}
                  <div
                    data-testid={`status-indicator-${item.scope_id}`}
                    className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      isWaitingOnUs ? 'bg-sky-500' : 'bg-violet-500'
                    }`}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {item.project_number ? `P-${item.project_number}` : 'Proposal'}: {item.scope_name}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {item.organization_name}
                        </div>
                      </div>
                      <div
                        className={`text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0 ${
                          item.days_waiting > 7
                            ? 'text-rose-700 bg-rose-100'
                            : item.days_waiting > 3
                            ? 'text-amber-700 bg-amber-100'
                            : 'text-gray-600 bg-gray-100'
                        }`}
                      >
                        {item.days_waiting}d
                      </div>
                    </div>
                    {item.status_notes && (
                      <div className="text-xs text-gray-500 mt-1 truncate">{item.status_notes}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        !hasAnyActions && (
          <div className="border-t pt-4 text-center text-gray-500">
            <p className="text-sm">All caught up! No urgent items.</p>
          </div>
        )
      )}
    </div>
  );
}

function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}k`;
  } else {
    return `$${amount.toFixed(0)}`;
  }
}
